import { useState, useRef, useEffect } from 'react'
import { Button, Input } from '../common'
import type { Client, ClientInsert } from '../../types/database.types'
import { useClientStore } from '../../stores'

interface QuickAddClientFormProps {
  providerId: string
  providerName: string
  onSuccess: (client: Client) => void
  onCancel: () => void
  initialName?: string
  initialEmail?: string
}

export function QuickAddClientForm({
  providerId,
  providerName,
  onSuccess,
  onCancel,
  initialName = '',
  initialEmail = '',
}: QuickAddClientFormProps) {
  const [fullName, setFullName] = useState(initialName)
  const [email, setEmail] = useState(initialEmail)
  const [sendInvitation, setSendInvitation] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ fullName?: string; email?: string; general?: string }>({})

  const nameInputRef = useRef<HTMLInputElement>(null)
  const { createClient, fetchClients } = useClientStore()

  // Auto-focus name field on mount
  useEffect(() => {
    nameInputRef.current?.focus()
  }, [])

  // Clear field errors on change
  const handleNameChange = (value: string) => {
    setFullName(value)
    if (errors.fullName) {
      setErrors((prev) => ({ ...prev, fullName: undefined }))
    }
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {}

    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required'
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)
    setErrors({})

    try {
      const clientData: ClientInsert = {
        provider_id: providerId,
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        status: 'invited',
      }

      const newClient = await createClient({
        clientData,
        sendInvitation,
        providerName,
      })

      if (newClient) {
        // Refresh the clients list so it appears in AssignmentStep
        await fetchClients(providerId)
        onSuccess(newClient)
      } else {
        setErrors({ general: 'Failed to create client. Please try again.' })
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create client'

      // Check for duplicate email error
      if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('already exists')) {
        setErrors({ email: 'A client with this email already exists' })
      } else {
        setErrors({ general: errorMessage })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errors.general && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {errors.general}
        </div>
      )}

      <Input
        ref={nameInputRef}
        label="Full Name"
        value={fullName}
        onChange={(e) => handleNameChange(e.target.value)}
        placeholder="John Smith"
        error={errors.fullName}
        disabled={isLoading}
        autoComplete="off"
      />

      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => handleEmailChange(e.target.value)}
        placeholder="john@example.com"
        error={errors.email}
        disabled={isLoading}
        autoComplete="off"
      />

      <div className="flex items-start gap-3 pt-2">
        <input
          type="checkbox"
          id="quickAddSendInvitation"
          checked={sendInvitation}
          onChange={(e) => setSendInvitation(e.target.checked)}
          disabled={isLoading}
          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="quickAddSendInvitation" className="text-sm">
          <span className="font-medium text-gray-700">Send invitation email</span>
          <p className="text-gray-500">
            The client will receive an email with a link to view their assigned programs
          </p>
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          disabled={!fullName.trim() || !email.trim()}
        >
          Add Client
        </Button>
      </div>
    </form>
  )
}
